import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { useAtom } from 'jotai'
import { Button } from '../../ui/Button/Button'
import { EditFill, SendFill, SuitcaseFill } from '../../../assets/icons'
import { Circle } from '../../ui/Card'
import { CircleIcon } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import { IUpdateMemberData, Member } from '../../../models/Member'
import { getWorkplacesAndPositions } from '../../../api/misc'
import { colors } from '../../../theme/palette'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { baseButton } from '../../../theme/reuseables'
import { Overlay } from '../../ui/Sidebar'
import { Dialog } from '../../ui/Dialog/Dialog'
import { cancelMemberFromWorkplace } from '../../../api/members/members'
import { Workplace } from '../../../models/Workplace'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { FormTextArea } from '../FormElements/FormInput'
import { L, L_HTML } from '../../providers/Lokalise'
import Input from '../../ui/FormElememts/Input'
import { Select } from '../../ui/FormElememts/Select'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'
import { profileAtom } from '../../../api/profile/profile'

interface EditProps {
    member: Member
    onOpen?: () => void
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    OevrigeOplysninger?: string
}

export const CancelWorkplace: FC<EditProps> = ({ member, onOpen }) => {
    const { register, reset, formState, handleSubmit } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [, setWorkplaces] = useState<Workplace[]>([])
    const [selectedDate, _setSelectedDate] = useState(null)
    const [isOpen, setIsOpen] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    // const [values, setValues] = useState<RegistrationFormFields>({
    //     OevrigeOplysninger: undefined,
    // })
    const [addInfo, setAddInfo] = useState(undefined)
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        string | undefined
    >(undefined)

    const getPositions = async (): Promise<void> => {
        // if (member.workPlaces.length && member.workPlaces.length === 1) {
        //     setSelectedWorkplace(String(member.workPlaces[0].number))
        // }

        const response = await getWorkplacesAndPositions()

        let workplaces = response.workPlaces

        workplaces = workplaces.filter((place) => {
            return member.workPlaces.some(
                (work) => work.number !== place.number
            )
        })
        setWorkplaces(workplaces)
    }

    useEffect(() => {
        getPositions()

        if (member.workPlaces.length === 1) {
            setSelectedWorkplace(String(member.primaryWorkPlace.number))
        }
    }, [])

    useEffect(() => {
        let valid = false

        valid = !!selectedDate && !!selectedWorkplace
        setIsValid(valid)
    }, [selectedWorkplace, selectedDate])

    const resetForm = (): void => {
        if (member.workPlaces.length && member.workPlaces.length === 1) {
            setSelectedWorkplace(String(member.workPlaces[0].number))
        } else {
            setSelectedWorkplace(undefined)
        }

        _setSelectedDate(null)
        setAddInfo(undefined)
        reset()
    }

    const { isDirty, dirtyFields } = formState

    const submitHandler = async (
        data: RegistrationFormFields
    ): Promise<string> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return Promise.resolve('OK')
        }
        const fieldsToEdit: IUpdateMemberData = {
            Medlemsident: member.memberId,
            Aendringsdato: moment(selectedDate).format('YYYY-MM-DD HH:mm:ss'),
            Arbejdsstednummer: Number(selectedWorkplace),
        }

        if (dirtyFields.OevrigeOplysninger) {
            fieldsToEdit.OevrigeOplysninger = data.OevrigeOplysninger
        }

        const response = await cancelMemberFromWorkplace(fieldsToEdit)

        if (response) {
            setIsOpen(false)
            setShowDialog(true)
            resetForm()
        }

        return Promise.resolve('OK')
    }

    return (
        <>
            <Button
                type="action"
                onClick={() => {
                    setIsOpen(true)
                    if (onOpen) onOpen()
                }}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignContent: 'start',
                    padding: 0,
                    justifyContent: 'left',
                }}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon iconWidth="16px">
                        <EditFill />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="XS" spacing=".2rem">
                    {L('MINEMEDLEMMER_DETALJE_STOPPET-PÅ-ARBEDJSPLADS_KNAP')}
                </Typography>
            </Button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                    resetForm()
                }}
            >
                <DrawerHeaderMemberInfo member={member} />
                <form
                    className={classes.contentWrapper}
                    onSubmit={handleSubmit(submitHandler)}
                >
                    <Typography
                        tag="h2"
                        size="XL"
                        align="center"
                        weight="bold"
                        style={{ margin: '20px 0 10px' }}
                    >
                        {L_HTML(
                            'MINEMEDLEMMER_DETALJE_STOPPET-PÅ-ARBEDJSPLADS_TITEL'
                        )}
                    </Typography>
                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon>
                                <EditFill />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L_HTML('GENERISK_FORM_ANSÆTTELSE_TITEL')}
                        </Typography>
                    </FormHeader>
                    {/* <FormControl>
                        <select
                            className={classes.select}
                            value={selectedWorkplace || null}
                            onChange={(e) => {
                                setSelectedWorkplace(e.target.value)
                            }}
                        >
                            <option value="default" hidden>
                                Arbejdssted *
                            </option>
                            {member.workPlaces.map((work) => {
                                return (
                                    <option value={work.number}>
                                        {work.name}
                                    </option>
                                )
                            })}
                        </select>
                    </FormControl> */}
                    {member && (
                        <FormControl>
                            {member.workPlaces.length > 1 ? (
                                <Select
                                    placeholder="Arbejdssted"
                                    onChange={(e) => {
                                        setSelectedWorkplace(e.target.value)
                                    }}
                                    options={member.workPlaces.map((wp) => {
                                        const o = {
                                            label: wp.name,
                                            value: String(wp.number),
                                        }

                                        return o
                                    })}
                                />
                            ) : (
                                <Input
                                    disabled
                                    required={false}
                                    label={member.primaryWorkPlace.name}
                                />
                            )}
                        </FormControl>
                    )}
                    <FormControl>
                        <DatePickerComponent
                            label="Jeg ved medlemmet er ophørt pr."
                            selected={selectedDate}
                            minDate={moment().subtract(1, 'year').toDate()}
                            setSelected={(e) => {
                                _setSelectedDate(e)
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormTextArea
                            label={L('GENERISK_FORM_ØVRIGE-OPLYSNINGER_LABEL')}
                            register={register}
                            name="OevrigeOplysninger"
                            required={false}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                resetForm()
                            }}
                            type="secondary"
                            size="large"
                        >
                            Annuller
                        </Button>
                        <Button buttonType="submit" type="primary" size="large">
                            Send
                        </Button>
                    </div>

                    <div />
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<SendFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_OVERSKRIFT')}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_TEXT')}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_KNAP')}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}
